import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const stageConfig = [
  { key:'lead', label:'Lead', bar:'bg-slate-400', color:'bg-slate-100 text-slate-600' },
  { key:'qualified', label:'Nitelikli', bar:'bg-blue-500', color:'bg-blue-100 text-blue-600' },
  { key:'proposal', label:'Teklif', bar:'bg-violet-500', color:'bg-violet-100 text-violet-600' },
  { key:'negotiation', label:'Muzakere', bar:'bg-amber-500', color:'bg-amber-100 text-amber-600' },
  { key:'won', label:'Kazanildi', bar:'bg-emerald-500', color:'bg-emerald-100 text-emerald-600' },
  { key:'lost', label:'Kaybedildi', bar:'bg-red-400', color:'bg-red-100 text-red-600' },
];


function formatDue(dateStr) {
  if (!dateStr) return null;
  const due = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((due.setHours(0,0,0,0) - now.setHours(0,0,0,0)) / 86400000);
  if (diff === 0) return { label: "Bugun", color: "text-red-500 font-semibold" };
  if (diff === 1) return { label: "Yarin", color: "text-amber-500 font-semibold" };
  if (diff < 0) return { label: "Gecikti", color: "text-red-600 font-bold" };
  return { label: due.toLocaleDateString("tr-TR", { day: "numeric", month: "long" }), color: "text-gray-500" };
}
const priCfg = {
  high:   { label: "Yuksek", cls: "bg-red-100 text-red-600 border border-red-200" },
  medium: { label: "Orta",   cls: "bg-amber-100 text-amber-600 border border-amber-200" },
  low:    { label: "Dusuk",  cls: "bg-gray-100 text-gray-500 border border-gray-200" },
};
function TaskTable({ tasks }) {
  const [checked, setChecked] = React.useState({});
  const toggle = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="text-base font-bold text-gray-800">Son Gorevler</h2>
        <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">{tasks.slice(0,6).length} gorev</span>
      </div>
      <div className="divide-y divide-gray-50">
        {tasks.slice(0, 6).map(task => {
          const done = checked[task.id] || task.status === "done";
          const due = formatDue(task.due_date);
          const pri = priCfg[task.priority] || priCfg.low;
          return (
            <div key={task.id} className={"flex items-center gap-4 px-6 py-3.5 transition-all duration-200 hover:bg-gray-50/70 " + (done ? "opacity-50" : "")}>
              <button onClick={() => toggle(task.id)}
                className={"w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 " +
                  (done ? "bg-emerald-500 border-emerald-500" : "border-gray-300 hover:border-emerald-400")}>
                {done && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                  </svg>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <p className={"text-sm font-medium truncate transition-all duration-200 " + (done ? "line-through text-gray-400" : "text-gray-800")}>{task.title}</p>
                {task.description && <p className="text-xs text-gray-400 truncate mt-0.5">{task.description}</p>}
              </div>
              <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0 w-24 justify-end">
                {due ? (
                  <span className={"text-xs " + due.color}>{due.label}</span>
                ) : (
                  <span className="text-xs text-gray-300">—</span>
                )}
              </div>
              <span className={"hidden sm:inline text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 " + pri.cls}>{pri.label}</span>
            </div>
          );
        })}
      </div>
      <div className="px-6 py-3 border-t border-gray-100 bg-gray-50/50">
        <button onClick={() => window.location.href="/tasks"} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
          Tum gorevleri gor →
        </button>
      </div>
    </div>
  );
}


// ─── Monthly Sales Chart ──────────────────────────────────────────────────────
const MONTHLY_DATA = [
  { ay: 'Oca', gelir: 42000, hedef: 50000 },
  { ay: 'Sub', gelir: 68000, hedef: 50000 },
  { ay: 'Mar', gelir: 55000, hedef: 60000 },
  { ay: 'Nis', gelir: 91000, hedef: 70000 },
  { ay: 'May', gelir: 78000, hedef: 70000 },
  { ay: 'Haz', gelir: 112000, hedef: 90000 },
  { ay: 'Tem', gelir: 89000, hedef: 90000 },
  { ay: 'Agu', gelir: 134000, hedef: 100000 },
  { ay: 'Eyl', gelir: 98000, hedef: 100000 },
  { ay: 'Eki', gelir: 145000, hedef: 120000 },
  { ay: 'Kas', gelir: 167000, hedef: 130000 },
  { ay: 'Ara', gelir: 0, hedef: 140000 },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-sm">
      <p className="font-bold text-gray-700 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: p.fill || p.color }} />
          <span className="text-gray-500">{p.name}:</span>
          <span className="font-semibold text-gray-800">{Number(p.value).toLocaleString("tr-TR")} TL</span>
        </div>
      ))}
    </div>
  );
}

function SalesChart() {
  const total = MONTHLY_DATA.reduce((s, d) => s + d.gelir, 0);
  const best = MONTHLY_DATA.reduce((a, b) => a.gelir > b.gelir ? a : b);
  const avg = Math.round(MONTHLY_DATA.filter(d => d.gelir > 0).reduce((s, d) => s + d.gelir, 0) / MONTHLY_DATA.filter(d => d.gelir > 0).length);

  return (
    <div className="card p-6">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-base font-bold text-gray-800">Aylik Satis Performansi</h2>
          <p className="text-gray-500 text-xs mt-0.5">2025 yili gelir ve hedef karsilastirmasi</p>
        </div>
        <div className="flex items-center gap-4 text-xs flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-indigo-500" />
            <span className="text-gray-500">Gerceklesen</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-gray-200" />
            <span className="text-gray-500">Hedef</span>
          </div>
        </div>
      </div>

      {/* Mini Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: "Toplam Gelir", value: total.toLocaleString("tr-TR") + " TL", color: "text-indigo-600" },
          { label: "En Iyi Ay", value: best.ay + " · " + best.gelir.toLocaleString("tr-TR") + " TL", color: "text-emerald-600" },
          { label: "Aylik Ort.", value: avg.toLocaleString("tr-TR") + " TL", color: "text-amber-600" },
        ].map((s, i) => (
          <div key={i} className="bg-gray-50 rounded-xl px-3 py-2.5">
            <div className={"text-sm font-bold " + s.color}>{s.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={MONTHLY_DATA} barGap={4} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="ay" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false}
              tickFormatter={v => v >= 1000 ? (v/1000) + "K" : v} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc", radius: 6 }} />
            <Bar dataKey="hedef" name="Hedef" fill="#e2e8f0" radius={[4, 4, 0, 0]} maxBarSize={20} />
            <Bar dataKey="gelir" name="Gerceklesen" radius={[4, 4, 0, 0]} maxBarSize={20}>
              {MONTHLY_DATA.map((entry, index) => (
                <Cell key={index}
                  fill={entry.gelir >= entry.hedef ? "#6366f1" : entry.gelir > 0 ? "#818cf8" : "#e2e8f0"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [pipeline, setPipeline] = useState({});
  const [customers, setCustomers] = useState([]);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    api.get('/deals/pipeline/summary').then(r => setPipeline(r.data)).catch(() => {});
    api.get('/customers').then(r => setCustomers(r.data)).catch(() => {});
    api.get('/tasks').then(r => setTasks(r.data)).catch(() => {});
  }, []);

  const roleMap = { super_admin:'Super Admin', office_sales:'Ofis Satis', field_sales:'Saha Satis' };
  const pending = tasks.filter(t => t.status === 'pending').length;
  const wonRevenue = pipeline.won?.total_value || 0;
  const totalDeals = Object.values(pipeline).reduce((s, v) => s + (v?.count || 0), 0);
  const maxCount = Math.max(...Object.values(pipeline).map(v => v?.count || 0), 1);
  const totalForBar = Object.values(pipeline).reduce((s, v) => s + (v?.count || 0), 0) || 1;
  const today = new Date().toLocaleDateString('tr-TR', { weekday:'long', day:'numeric', month:'long' });

  return (
    <div className="space-y-6 animate-fadein">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Merhaba, {user?.full_name?.split(' ')[0]} 👋</h1>
          <p className="text-gray-500 text-sm mt-0.5">{today} — {roleMap[user?.role]}</p>
        </div>
        <div className="card px-4 py-2 flex items-center gap-2 text-sm text-gray-600">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Sistem aktif
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon:'👥', label:'Toplam Musteri', value: customers.length, color:'bg-blue-50', trend:12, sub:'bu ay' },
          { icon:'💼', label:'Toplam Deal', value: totalDeals, color:'bg-indigo-50', trend:8, sub:'bu hafta' },
          { icon:'✅', label:'Bekleyen Gorev', value: pending, color:'bg-amber-50', trend:-3, sub:'dun gore' },
          { icon:'💰', label:'Kazanilan Gelir', value: wonRevenue.toLocaleString('tr-TR') + ' TL', color:'bg-emerald-50', trend:24, sub:'bu ay' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-default group">
            <div className="flex items-start justify-between mb-4">
              <div className={"w-11 h-11 rounded-xl flex items-center justify-center text-xl ring-4 transition-all duration-300 group-hover:scale-110 " + s.color + " ring-gray-50"}>{s.icon}</div>
              <div className={"flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold " + (s.trend >= 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500")}>
                {s.trend >= 0 ? "▲" : "▼"} {Math.abs(s.trend)}%
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-800 leading-tight">{s.value}</div>
            <div className="flex items-center justify-between mt-1">
              <div className="text-sm text-gray-500">{s.label}</div>
              <div className={"text-xs font-medium " + (s.trend >= 0 ? "text-emerald-500" : "text-red-400")}>{s.trend >= 0 ? "+" : ""}{s.trend}% {s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-6">
        <h2 className="text-base font-bold text-gray-800 mb-5">Satis Pipeline</h2>
        <div className="space-y-3">
          {stageConfig.map(s => {
            const count = pipeline[s.key]?.count || 0;
            const val = pipeline[s.key]?.total_value || 0;
            return (
              <div key={s.key} className="flex items-center gap-4">
                <div className="w-24 text-right">
                  <span className={"text-xs font-semibold px-2 py-1 rounded-full " + s.color}>{s.label}</span>
                </div>
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={"h-full rounded-full transition-all duration-700 " + s.bar} style={{ width: Math.min((count / totalForBar * 100) * 1.5, 100) + '%', minWidth: count > 0 ? '6px' : '0' }} />
                </div>
                <span className="text-sm font-bold text-gray-800 w-6">{count}</span>
                {val > 0 && <span className="text-xs text-gray-400 hidden lg:block w-28">{val.toLocaleString('tr-TR')} TL</span>}
              </div>
            );
          })}
        </div>
      </div>

      <SalesChart />
      {tasks.length > 0 && <TaskTable tasks={tasks} />}
    </div>
  );
}
